import { tr } from '@/lib/i18n-core';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { Baby, Ban, BookUser, FileX2, IdCard, UserX, type LucideIcon } from 'lucide-react';
import {
  ID_MISSING_REASONS,
  detectIdType,
  documentRule,
  fromHijri,
  impliedNationality,
  toHijri,
  type CountryProfile,
  type FacilityKind,
  type IdentityDocType,
  type IdentityDocumentRule,
  type IdMissingReason,
  type RegisterPatientInput,
} from '@his/shared';
import { DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { isoCountries } from '@/lib/country';
import { useAuth } from '@/lib/auth';
import { ChoiceButtons, type ChoiceOption as Choice } from '@/components/ui/choice-buttons';

const MISSING_REASON_ICON: Record<IdMissingReason, LucideIcon> = {
  unidentified: UserX,
  newborn: Baby,
  not_held: FileX2,
  refused: Ban,
};

const MOST_BUTTONS = 5;

export function missingReasonLabel(reason: IdMissingReason): string {
  return tr('opd', `id_missing_${reason}`);
}

type Mode = 'usual' | 'other' | 'none';

function startingMode(idType: string | undefined, usual: IdentityDocumentRule[]): Mode {
  if (idType === 'no_document') return 'none';
  if (idType && !usual.some((document) => document.type === idType)) return 'other';
  return 'usual';
}

export function IdentityDocumentFields({
  form,
  profile,
  facility,
  required,
  autoFocus,
  essentialsOnly,
  onBlurNumber,
}: {
  form: UseFormReturn<RegisterPatientInput>;
  profile: CountryProfile;
  facility: FacilityKind;
  required?: boolean;
  autoFocus?: boolean;
  essentialsOnly?: boolean;
  onBlurNumber?: () => void;
}) {
  const capture = profile.identity.capture[facility];
  const { hasModule } = useAuth();
  const usualDocuments = useMemo(
    () => profile.identity.documents.filter((document) => document.type !== 'passport' && document.type !== 'no_document'),
    [profile],
  );
  const usualDefault = profile.identity.defaultType === 'passport' ? usualDocuments[0]?.type ?? 'passport' : profile.identity.defaultType;
  const [mode, setMode] = useState<Mode>(() => startingMode(form.getValues('idType'), usualDocuments));
  const idType = useWatch({ control: form.control, name: 'idType' }) as IdentityDocType | undefined;
  const cnic = useWatch({ control: form.control, name: 'cnic' });
  const errors = form.formState.errors;
  const rule = idType ? documentRule(profile, idType) : null;
  const numberField = form.register('cnic');

  useEffect(() => {
    if (capture !== 'hidden' && !idType) form.setValue('idType', usualDefault);
  }, [capture, idType, usualDefault, form]);

  useEffect(() => {
    if (mode !== 'usual' || !profile.identity.autoDetect || !cnic) return;
    const detected = detectIdType(profile, cnic);
    if (detected !== idType && usualDocuments.some((document) => document.type === detected)) form.setValue('idType', detected);
  }, [cnic, mode, profile, idType, usualDocuments, form]);

  if (capture === 'hidden') return null;

  const implied = impliedNationality(profile, idType ?? null);
  const countries = isoCountries('en');
  const documents = mode === 'other' ? profile.identity.documents.filter((document) => document.type !== 'no_document') : usualDocuments;
  const documentChoices: Choice<IdentityDocType>[] = documents.map((document) => ({ value: document.type, label: document.label }));
  const modeChoices: Choice<Mode>[] = [
    { value: 'usual', label: usualDocuments.length === 1 ? usualDocuments[0].label : tr('opd', 'id_mode_usual'), icon: IdCard },
    { value: 'other', label: tr('opd', 'id_mode_other'), icon: BookUser },
    { value: 'none', label: tr('opd', 'no_document'), icon: FileX2 },
  ];
  const reasonChoices: Choice<IdMissingReason>[] = ID_MISSING_REASONS.map((reason) => ({ value: reason, label: missingReasonLabel(reason), icon: MISSING_REASON_ICON[reason] }));

  const chooseMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    if (next === 'none') {
      form.setValue('idType', 'no_document', { shouldDirty: true });
      form.setValue('cnic', undefined, { shouldDirty: true });
      return;
    }
    form.setValue('idType', next === 'other' ? 'passport' : usualDefault, { shouldDirty: true });
    form.setValue('idMissingReason', undefined, { shouldDirty: true });
  };

  return (
    <>
      <Field label={tr('opd', 'document')} className="sm:col-span-2">
        <ChoiceButtons value={mode} onChange={chooseMode} options={modeChoices} />
      </Field>
      {mode === 'none' ? (
        <Controller
          control={form.control}
          name="idMissingReason"
          render={({ field }) => (
            <Field label={tr('opd', 'why_is_there_no_document')} required error={errors.idMissingReason?.message} className="sm:col-span-2">
              <ChoiceButtons value={field.value} onChange={field.onChange} onBlur={field.onBlur} options={reasonChoices} layout="cards" required />
            </Field>
          )}
        />
      ) : (
        <>
          {(mode === 'other' || usualDocuments.length > 1) && (
            <Controller
              control={form.control}
              name="idType"
              render={({ field }) => (
                <Field label={tr('opd', 'document_type')} className="sm:col-span-2">
                  {documentChoices.length <= MOST_BUTTONS ? (
                    <ChoiceButtons value={field.value} onChange={field.onChange} options={documentChoices} layout="chips" />
                  ) : (
                    <Select value={field.value ?? ''} onChange={(event) => field.onChange(event.target.value)}>
                      {documentChoices.map((document) => (
                        <option key={document.value} value={document.value}>
                          {document.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              )}
            />
          )}
          <Field
            label={rule?.label ?? tr('opd', 'id_number')}
            required={required}
            error={errors.cnic?.message}
            hint={rule?.example ? tr('opd', 'for_example_x', { example: rule.example }) : undefined}
          >
            <Input
              dir="ltr"
              autoComplete="off"
              spellCheck={false}
              className="font-mono tracking-wide"
              autoFocus={autoFocus}
              {...numberField}
              onBlur={(event) => {
                void numberField.onBlur(event);
                onBlurNumber?.();
              }}
              placeholder={rule?.example}
            />
          </Field>
          {rule?.needsIssuingCountry && (
            <Field label={tr('opd', 'issuing_country')} required error={errors.idCountry?.message}>
              <Select {...form.register('idCountry')}>
                <option value="">{tr('opd', 'choose_2')}</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {idType === 'passport' && (
            <Controller
              control={form.control}
              name="idExpiry"
              render={({ field }) => (
                <Field label={tr('opd', 'passport_expiry')} error={errors.idExpiry?.message}>
                  <DatePicker value={field.value ?? ''} onChange={(value) => field.onChange(value || undefined)} />
                </Field>
              )}
            />
          )}
        </>
      )}
      {profile.identity.askNationality && !implied && mode !== 'none' && !essentialsOnly && (
        <Field label={tr('opd', 'nationality')} error={errors.nationality?.message}>
          <Select {...form.register('nationality')}>
            <option value="">{tr('opd', 'not_recorded')}</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {hasModule('nphies') && !essentialsOnly && (
        <Field label={tr('opd', 'occupation_code')} hint={tr('opd', 'the_occupation_the_insurer_records_for_nphies')} error={errors.occupationCode?.message}>
          <Input {...form.register('occupationCode')} />
        </Field>
      )}
    </>
  );
}

export function identityForSubmit(values: RegisterPatientInput, profile: CountryProfile, facility: FacilityKind, adult: boolean): { values: RegisterPatientInput; problem: string | null } {
  const hasNumber = Boolean(values.cnic?.trim());
  if (values.idType === 'no_document') return { values: { ...values, cnic: undefined }, problem: values.idMissingReason ? null : tr('opd', 'say_why_the_patient_has_no_document') };
  if (hasNumber) return { values, problem: null };
  const stripped = { ...values, idType: undefined, idCountry: undefined, idExpiry: undefined };
  const required = profile.identity.capture[facility] === 'required' && adult;
  return { values: stripped, problem: required ? tr('opd', 'record_an_identity_document_or_no_document') : null };
}

export function HijriDobField({ value, onChange }: { value: string | undefined; onChange: (iso: string) => void }) {
  const current = value ? toHijri(new Date(`${value}T00:00:00.000Z`)) : null;
  const [parts, setParts] = useState({ day: current?.day ?? '', month: current?.month ?? '', year: current?.year ?? '' });
  const [problem, setProblem] = useState<string | null>(null);

  const apply = (next: typeof parts) => {
    setParts(next);
    const [day, month, year] = [Number(next.day), Number(next.month), Number(next.year)];
    if (!day || !month || !year) return;
    const date = fromHijri(year, month, day);
    if (!date) {
      setProblem(tr('opd', 'that_hijri_date_does_not_exist'));
      return;
    }
    setProblem(null);
    onChange(date.toISOString().slice(0, 10));
  };

  return (
    <Field label={tr('opd', 'date_of_birth_hijri')} error={problem ?? undefined} hint={value ? tr('opd', 'gregorian_x', { value: value }) : tr('opd', 'day_month_year_umm_al_qura')}>
      <div className="flex gap-2" dir="ltr">
        <Input aria-label={tr('opd', 'day')} inputMode="numeric" className="w-16" value={parts.day} onChange={(event) => apply({ ...parts, day: event.target.value })} />
        <Input aria-label={tr('opd', 'month')} inputMode="numeric" className="w-16" value={parts.month} onChange={(event) => apply({ ...parts, month: event.target.value })} />
        <Input aria-label={tr('opd', 'year')} inputMode="numeric" className="w-24" value={parts.year} onChange={(event) => apply({ ...parts, year: event.target.value })} />
      </div>
    </Field>
  );
}
